'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Phone } from 'lucide-react';
import { toast } from 'sonner';


interface LoginFormData {
  emailOrPhone: string;
  password: string;
  rememberMe: boolean;
}

interface SignUpFormData {
  fullName: string;
  phone: string;
  email: string;
  password: string;
  confirmPassword: string;
  referralCode: string;
  agreeTerms: boolean;
}

export default function AuthForm() {
  const [tab, setTab] = useState<'login' | 'signup'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loginForm = useForm<LoginFormData>({ mode: 'onBlur' });
  const signupForm = useForm<SignUpFormData>({ mode: 'onBlur' });

  const handleLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    // Backend integration point: POST /api/auth/login with data
    await new Promise((r) => setTimeout(r, 1500));
    setIsLoading(false);
    toast.success('Welcome back! Redirecting to dashboard...');
    setTimeout(() => { window.location.href = '/user-dashboard'; }, 1000);
  };

  const handleSignUp = async (data: SignUpFormData) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    // Backend integration point: POST /api/auth/register with data
    await new Promise((r) => setTimeout(r, 1800));
    setIsLoading(false);
    toast.success('Account created! Welcome to DakaPay.');
    setTimeout(() => { window.location.href = '/user-dashboard'; }, 1000);
  };

  return (
    <div className="bg-card rounded-2xl border border-border shadow-card p-8">
      {/* Tabs */}
      <div className="flex bg-muted rounded-xl p-1 mb-7">
        {(['login', 'signup'] as const).map((t) => (
          <button
            key={`auth-tab-${t}`}
            onClick={() => setTab(t)}
            className={`flex-1 py-2.5 text-sm font-600 rounded-lg transition-all duration-200 ${
              tab === t ? 'bg-card shadow-card text-primary' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        ))}
      </div>

      {tab === 'login' ? (
        <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-5">
          <div>
            <label className="block text-sm font-500 text-foreground mb-1.5">Email or Phone Number</label>
            <input
              type="text"
              placeholder="Enter email or phone"
              {...loginForm.register('emailOrPhone', { required: 'Email or phone is required' })}
              className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
            />
            {loginForm.formState.errors.emailOrPhone && (
              <p className="text-danger text-xs mt-1">{loginForm.formState.errors.emailOrPhone.message}</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-500 text-foreground">Password</label>
              <a href="#" className="text-xs text-accent hover:underline">Forgot password?</a>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                {...loginForm.register('password', {
                  required: 'Password is required',
                  minLength: { value: 6, message: 'Minimum 6 characters' },
                })}
                className="w-full bg-input border border-border rounded-xl px-4 py-3 pr-11 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {loginForm.formState.errors.password && (
              <p className="text-danger text-xs mt-1">{loginForm.formState.errors.password.message}</p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="rememberMe"
              {...loginForm.register('rememberMe')}
              className="w-4 h-4 accent-accent rounded"
            />
            <label htmlFor="rememberMe" className="text-sm text-muted-foreground cursor-pointer">
              Remember me for 30 days
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full btn-primary py-3.5 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign In to DakaPay'
            )}
          </button>
        </form>
      ) : (
        <form onSubmit={signupForm.handleSubmit(handleSignUp)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-500 text-foreground mb-1.5">Full Name</label>
              <input
                type="text"
                placeholder="e.g. Chukwuemeka Obi"
                {...signupForm.register('fullName', { required: 'Full name is required' })}
                className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
              />
              {signupForm.formState.errors.fullName && (
                <p className="text-danger text-xs mt-1">{signupForm.formState.errors.fullName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Phone Number</label>
              <div className="flex">
                <span className="bg-muted border border-r-0 border-border rounded-l-xl px-3 flex items-center text-sm text-muted-foreground whitespace-nowrap">
                  🇳🇬 +234
                </span>
                <input
                  type="tel"
                  placeholder="0803 456 7890"
                  {...signupForm.register('phone', {
                    required: 'Phone is required',
                    pattern: { value: /^0[789][01]\d{8}$/, message: 'Enter a valid Nigerian number' },
                  })}
                  className="flex-1 bg-input border border-border rounded-r-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                />
              </div>
              {signupForm.formState.errors.phone && (
                <p className="text-danger text-xs mt-1">{signupForm.formState.errors.phone.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Email Address</label>
              <input
                type="email"
                placeholder="you@email.com"
                {...signupForm.register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
                className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
              />
              {signupForm.formState.errors.email && (
                <p className="text-danger text-xs mt-1">{signupForm.formState.errors.email.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min. 8 characters"
                  {...signupForm.register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Minimum 8 characters' },
                  })}
                  className="w-full bg-input border border-border rounded-xl px-4 py-3 pr-11 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {signupForm.formState.errors.password && (
                <p className="text-danger text-xs mt-1">{signupForm.formState.errors.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Repeat password"
                  {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                  className="w-full bg-input border border-border rounded-xl px-4 py-3 pr-11 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {signupForm.formState.errors.confirmPassword && (
                <p className="text-danger text-xs mt-1">{signupForm.formState.errors.confirmPassword.message}</p>
              )}
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-500 text-foreground mb-1.5">
                Referral Code <span className="text-muted-foreground font-400">(optional)</span>
              </label>
              <input
                type="text"
                placeholder="e.g. DAKA-XY29"
                {...signupForm.register('referralCode')}
                className="w-full bg-input border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
              />
              <p className="text-muted-foreground text-xs mt-1">Enter a referral code to earn ₦200 bonus on your first transaction</p>
            </div>
          </div>

          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="agreeTerms"
              {...signupForm.register('agreeTerms', { required: 'You must accept the terms' })}
              className="w-4 h-4 accent-accent rounded mt-0.5 shrink-0"
            />
            <label htmlFor="agreeTerms" className="text-xs text-muted-foreground cursor-pointer leading-relaxed">
              I agree to DakaPay's{' '}
              <a href="#" className="text-accent hover:underline">Terms of Service</a>,{' '}
              <a href="#" className="text-accent hover:underline">Privacy Policy</a>, and{' '}
              <a href="#" className="text-accent hover:underline">AML Policy</a>
            </label>
          </div>
          {signupForm.formState.errors.agreeTerms && (
            <p className="text-danger text-xs">{signupForm.formState.errors.agreeTerms.message}</p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full btn-primary py-3.5 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Creating account...
              </>
            ) : (
              'Create My Account'
            )}
          </button>
        </form>
      )}
    </div>
  );
}